import type { Strictness } from "@ast-grep/napi/types/rule";
import { useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

import type {
  StudioMode,
  StudioSource,
  StudioState,
  SupportedLang,
  TransientRepositorySchemaType,
} from "@acme/validators";

import { defaultState, getDefaultState } from "./_utils/get-store-defaults";
import { useAstGrepRemoteRunFiles } from "./use-ast-grep-remote-run";

export interface ExtendedStudioState extends StudioState {
  dirty: boolean;
  currentSessionId: string | null;
  selectedRepositories?: TransientRepositorySchemaType[];
  activeTestSourceId: number;
  activeRemoteRunFileId: string | null;
  leftPanelCollapsed: boolean;
  leftPanelTab: string;
  ruleErrors?: string;
}

interface StudioStore extends ExtendedStudioState {
  resetState: (lang: SupportedLang) => void;
  setMode: (mode: StudioMode) => void;
  setJsAstGrepCodemod: (query: string) => void;
  setRewrite: (rewrite: string) => void;
  setConfig: (config: string) => void;
  setSource: (source: string, sourceId: number) => void;
  setStrictness: (strictness: Strictness) => void;
  setSelector: (selector: string) => void;
  setActiveTestSourceId: (index: number) => void;
  overrideLang: (lang: SupportedLang) => void;
  addSource: (source: string) => void;
  removeSource: (index: number) => void;
  getCurrentState: () => StudioState;
  replaceSources: (sources: StudioSource[]) => void;
  setSources: (sources: StudioSource[]) => void;
  addAutoGeneratedSources: (sources: StudioSource[]) => void;
  // Session management
  setCurrentSessionId: (id: string | null) => void;
  setDirty: (dirty: boolean) => void;
  loadSessionState: (state: Partial<StudioState>) => void;
  // Repository selection
  setSelectedRepositories: (
    repositories: TransientRepositorySchemaType[],
  ) => void;
  setActiveRemoteRunFileId: (id: string | null) => void;

  // Send message data
  getSendMessageData: () => {
    currentYaml: string;
    currentJavaScript: string;
    language: SupportedLang;
  };

  // Left panel
  setLeftPanelCollapsed: (collapsed: boolean) => void;
  setLeftPanelTab: (tab: string) => void;
  // Set Rule errors
  setRuleErrors: (error: string) => void;
  clearRuleErrors: () => void;
}

const LOCAL_STORAGE_KEY = "ast-grep-studio-state";

export const useStudioStore = create<StudioStore>()(
  persist(
    devtools((set, get) => ({
      ...defaultState,
      resetState: (lang: SupportedLang) =>
        set(
          { ...defaultState, ...getDefaultState(lang), dirty: false },
          undefined,
          "resetState",
        ),
      setMode: (mode) => set({ mode, dirty: true }, undefined, "setMode"),
      setJsAstGrepCodemod: (query) =>
        set(
          { jsAstGrepCodemod: query, dirty: true },
          undefined,
          "setJsAstGrepCodemod",
        ),
      setRewrite: (rewrite) =>
        set({ rewrite, dirty: true }, undefined, "setRewrite"),
      setConfig: (config) =>
        set({ config, dirty: true }, undefined, "setConfig"),
      setStrictness: (strictness) =>
        set({ strictness, dirty: true }, undefined, "setStrictness"),
      setSelector: (selector) =>
        set({ selector, dirty: true }, undefined, "setSelector"),
      overrideLang: (lang) =>
        set({ lang, dirty: true }, undefined, "overrideLang"),
      setActiveTestSourceId: (id) =>
        set(
          { activeTestSourceId: id, dirty: true },
          undefined,
          "setCurrentSourceId",
        ),
      getCurrentState: () => {
        const fullState = get();
        const {
          currentSessionId: _currentSessionId,
          dirty: _dirty,
          activeTestSourceId: _currentSourceId,
          activeRemoteRunFileId: _currentRemoteRunFile,
          leftPanelCollapsed: _leftPanelCollapsed,
          leftPanelTab: _leftPanelTab,
          ruleErrors: _ruleErrors,
          selectedRepositories: _selectedRepositories,
          ...trimmedState
        } = fullState;
        return trimmedState as StudioState;
      },
      addSource: (source) => {
        const sources = get().sources;
        const newId = (sources[sources.length - 1]?.id ?? 0) + 1;
        set(
          {
            sources: [...sources, { source, id: newId }],
            dirty: true,
            activeTestSourceId: newId,
          },
          undefined,
          "addSource",
        );
      },
      removeSource: (id) => {
        set(
          {
            sources: get().sources.filter(
              ({ id: sourceId }) => sourceId !== id,
            ),
            dirty: true,
            activeTestSourceId:
              get().activeTestSourceId === id
                ? (get().sources[0]?.id ?? 1)
                : get().activeTestSourceId,
          },
          undefined,
          "removeSource",
        );
      },
      setSource: (source, sourceId) => {
        set(
          {
            dirty: true,
            sources: get().sources.map((item) =>
              item.id === sourceId
                ? { ...item, source, autoGenerated: false }
                : item,
            ),
          },
          undefined,
          "setSource",
        );
      },
      replaceSources: (sources) => {
        set(
          {
            sources,
            dirty: true,
            activeTestSourceId: sources[sources.length - 1]?.id ?? 1,
          },
          undefined,
          "replaceSources",
        );
      },
      setSources: (sources) => {
        set({ sources, dirty: true }, false, "setSources");
      },
      addAutoGeneratedSources: (sources) => {
        const previousSources = get().sources;
        // Remove all auto generated sources
        const newSources = previousSources.filter(
          (item) => !item.autoGenerated,
        );
        // Make sure the ids are unique
        const newSourcesWithUniqueIds = [...newSources, ...sources].map(
          (item, index) => ({
            ...item,
            id: index + 1,
          }),
        );

        set(
          {
            dirty: true,
            sources: newSourcesWithUniqueIds,
          },
          undefined,
          "addAutoGeneratedSources",
        );
      },
      // Session management
      setCurrentSessionId: (id) => {
        set({ currentSessionId: id }, undefined, "setCurrentSessionId");
      },
      setDirty: (dirty) => {
        set({ dirty }, undefined, "setDirty");
      },
      loadSessionState: (state) => {
        set(
          {
            ...state,
            dirty: false,
          },
          undefined,
          "loadSessionState",
        );
      },
      setSelectedRepositories: (repositories) => {
        set(
          { selectedRepositories: repositories },
          undefined,
          "setSelectedRepositories",
        );
      },
      setActiveRemoteRunFileId: (id) => {
        set(
          { activeRemoteRunFileId: id },
          undefined,
          "setActiveRemoteRunFileId",
        );
      },
      getSendMessageData: () => ({
        currentYaml: get().config,
        currentJavaScript: get().jsAstGrepCodemod,
        language: get().lang,
      }),
      setLeftPanelCollapsed: (collapsed) => {
        set(
          { leftPanelCollapsed: collapsed },
          undefined,
          "setLeftPanelCollapsed",
        );
      },
      setLeftPanelTab: (tab) => {
        set({ leftPanelTab: tab }, undefined, "setLeftPanelTab");
      },
      setRuleErrors: (error) => {
        set({ ruleErrors: error }, undefined, "setRuleErrors");
      },
      clearRuleErrors: () => {
        set({ ruleErrors: undefined }, undefined, "clearRuleErrors");
      },
    })),
    {
      name: LOCAL_STORAGE_KEY,
      version: 3,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

const useCurrentRemoteRunFile = () =>
  useStudioStore((state) => state.activeRemoteRunFileId);
const useCurrentTestSource = () =>
  useStudioStore((state) =>
    state.sources.find((item) => item.id === state.activeTestSourceId),
  );

export const useCurrentSource = () => {
  const remoteRunFiles = useAstGrepRemoteRunFiles();
  const remoteRunFile = useCurrentRemoteRunFile();
  const currentTestSource = useCurrentTestSource();
  return useMemo(() => {
    if (remoteRunFile) {
      const source = remoteRunFiles.get(
        remoteRunFile.replace(/@(\d+:\d+-?)+$/, ""),
      );
      if (source) {
        return source;
      }
    }
    return currentTestSource;
  }, [remoteRunFiles, currentTestSource, remoteRunFile]);
};
